/** Screen = a frame element. This module is the canonical, package-agnostic
 * source for the screen size presets and per-screen duration, shared by the
 * app-level screen dropdown and the in-editor properties panel. Kept in
 * `common` (no element dependency) so `packages/excalidraw` can import it. */

export type ScreenSizePreset = {
  name: string;
  ratio: string;
  width: number;
  height: number;
};

/** Popular social-media screen sizes (px). */
export const SCREEN_SIZE_PRESETS: ScreenSizePreset[] = [
  { name: "Instagram Post", ratio: "1:1", width: 1080, height: 1080 },
  { name: "Instagram Portrait", ratio: "4:5", width: 1080, height: 1350 },
  { name: "Story / Reel / TikTok", ratio: "9:16", width: 1080, height: 1920 },
  { name: "YouTube", ratio: "16:9", width: 1920, height: 1080 },
  { name: "X / Twitter", ratio: "16:9", width: 1600, height: 900 },
  { name: "Facebook / LinkedIn", ratio: "1.91:1", width: 1200, height: 630 },
  { name: "Pinterest Pin", ratio: "2:3", width: 1000, height: 1500 },
];

/** Index of the preset matching the given size, or -1 (custom). */
export const findPresetIndex = (width: number, height: number) =>
  SCREEN_SIZE_PRESETS.findIndex(
    (preset) =>
      Math.round(width) === preset.width &&
      Math.round(height) === preset.height,
  );

export const DEFAULT_SCREEN_DURATION_MS = 2000;

/** Per-frame screen duration for multi-screen sequences, persisted on
 * `frame.customData.screenDurationMs`. Falls back to the default on
 * missing/invalid values. Typed structurally to avoid an element dependency. */
export const getScreenDurationMs = (frame: {
  customData?: Record<string, any> | null;
}): number => {
  const value = frame.customData?.screenDurationMs;
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : DEFAULT_SCREEN_DURATION_MS;
};
